// Pure, React-free helpers for the Shift Detail panel: assignment-context classification,
// candidate-bucket enrichment, fit-score / KPI / time-range derivation, and the small display
// strings. Kept testable in isolation — the components just compose these. (No `pending` move
// status — a pending Works suggestion is `shift.suggested`, and review decisions live in an
// in-session override.)
import type { ColorsType } from '@common/tokens';
import type {
  ScheduleAssignedReasonType,
  ScheduleCandidateKpiType,
  ScheduleCandidateSignalType,
  ScheduleShiftOverrideType,
  ScheduleShiftSourceType,
  ScheduleShiftType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';
import type {
  ScheduleShiftCandidateRowType,
  ScheduleShiftCandidatesType,
} from '@load-balancing/graphql/queries/shift-candidates';

// Assignment label per shift source, matching the prototype's SOURCE_LABELS.
const SOURCE_LABELS: Record<ScheduleShiftSourceType, string> = {
  pattern: 'Pattern shift',
  self: 'Self-scheduled',
  float: 'Float',
  pto: 'PTO',
  open: 'Open slot',
  manual: 'Manually assigned',
  balancer: 'Works assigned',
};

export type ShiftContextKind =
  | 'open_slot'
  | 'existing'
  | 'suggestion_pending'
  | 'suggestion_accepted'
  | 'manual_edit';

export type ShiftContextType = {
  kind: ShiftContextKind;
  /** Resolved roster entry for the assigned/suggested worker, when known. */
  member: ScheduleTeamMemberType | null;
  /** Display-name fallback when the member can't be resolved from the roster. */
  workerName: string | null;
  /** Section heading above the worker row. */
  label: string;
  isPto: boolean;
  showUnassign: boolean;
  showAcceptReject: boolean;
};

function resolveMemberByName(
  name: string | undefined,
  team: ScheduleTeamMemberType[]
): ScheduleTeamMemberType | null {
  if (!name) return null;
  return team.find((member) => member.name === name) ?? null;
}

// Resolve a shift's assigned/suggested worker to a roster member by the authoritative worker
// uuid first, falling back to a display-name match. The id match survives a name mismatch
// between the shift's `workerName` and the roster member's `name` (WORK-6573), which previously
// left in-roster workers unresolved and so un-linked. Workers genuinely off this roster (floats,
// per-diem, cross-unit) still resolve to null — we have no profile record to open for them.
function resolveAssignedMember(
  shift: ScheduleShiftType,
  team: ScheduleTeamMemberType[]
): ScheduleTeamMemberType | null {
  if (shift.assignedWorkerId) {
    const byId = team.find((member) => member.id === shift.assignedWorkerId);
    if (byId) return byId;
  }
  return resolveMemberByName(shift.workerName, team);
}

function openContext(isPto: boolean): ShiftContextType {
  return {
    kind: 'open_slot',
    member: null,
    workerName: null,
    label: 'Assigned worker',
    isPto,
    showUnassign: false,
    showAcceptReject: false,
  };
}

/**
 * Derives the assignment context for a shift, layering the panel's in-session override
 * (manual assign / unassign / suggestion decision) over the base shift.
 */
export function classifyShift(
  shift: ScheduleShiftType,
  override: ScheduleShiftOverrideType,
  team: ScheduleTeamMemberType[]
): ShiftContextType {
  const isPto = shift.source === 'pto';

  if (override.assignedMemberId) {
    const member =
      team.find((entry) => entry.id === override.assignedMemberId) ?? null;
    return {
      kind: 'manual_edit',
      member,
      workerName: member?.name ?? null,
      label: 'Manually assigned',
      isPto: false,
      showUnassign: true,
      showAcceptReject: false,
    };
  }

  // Explicit unassign clears the slot.
  if (override.assignedMemberId === null) return openContext(isPto);

  if (shift.suggested && override.suggestionDecision !== 'rejected') {
    const accepted = override.suggestionDecision === 'accepted';
    const member = resolveAssignedMember(shift, team);
    return {
      kind: accepted ? 'suggestion_accepted' : 'suggestion_pending',
      member,
      workerName: shift.workerName ?? null,
      label: 'Works assigned',
      isPto,
      // Works-assigned shifts are assigned by us, not suggestions for review, so they
      // always offer Unassign like any other assigned shift.
      showUnassign: true,
      showAcceptReject: !accepted,
    };
  }

  // A rejected suggestion falls back to an open slot (the suggested worker was the only occupant).
  if (shift.suggested) return openContext(isPto);

  if (shift.workerName) {
    const member = resolveAssignedMember(shift, team);
    return {
      kind: 'existing',
      member,
      workerName: shift.workerName,
      label: SOURCE_LABELS[shift.source],
      isPto,
      showUnassign: !isPto,
      showAcceptReject: false,
    };
  }

  return openContext(isPto);
}

/** Fit-score color: green at/above 75, midnight 50–74, muted below. */
export function scoreColor(score: number): ColorsType {
  if (score >= 75) return 'grassDarkest';
  if (score >= 50) return 'midnightRegular';
  return 'midnightLighter';
}

/** Hours-to-FTE readout for a worker row; "—" until the roster's hours are populated. */
export function fteTextLabel(remainingHours: number | null): string {
  if (remainingHours === null) return '—';
  if (remainingHours < 0) return `${Math.abs(remainingHours)}h over FTE`;
  if (remainingHours === 0) return 'At FTE';
  return `${remainingHours}h to FTE`;
}

// Pads the start–end separator with a single space each side ("07:00–19:00" -> "07:00 – 19:00").
// Collapses any spaces the backend already sent around the dash first, so the result is consistent
// whatever the source format (and normalizes hyphen / en dash / em dash to a spaced en dash).
export function spaceTimeRange(timeRange: string): string {
  return timeRange.replace(/\s*[–—-]\s*/g, ' – ');
}

// A standard shift is 12h — the FTE delta one assignment moves a worker; used when an
// open synthesized row carries no real start/end instants to measure.
const STANDARD_SHIFT_HOURS = 12;

// Whole hours a shift spans, from its real instants; STANDARD_SHIFT_HOURS for an open
// synthesized row (no backing shift, so no instants).
export function shiftDurationHours(
  shift: Pick<ScheduleShiftType, 'startTime' | 'endTime'>
): number {
  if (!shift.startTime || !shift.endTime) return STANDARD_SHIFT_HOURS;
  const ms =
    new Date(shift.endTime).getTime() - new Date(shift.startTime).getTime();
  return Math.round(ms / 3_600_000);
}

// The shift's real local time range ("07:00–19:00"), from the zone-local startHour plus
// the measured duration (no client time zone needed). Falls back to the band's time when
// the shift carries no instants (open synthesized row); the header spaces the dash.
export function shiftTimeRange(
  shift: Pick<ScheduleShiftType, 'startHour' | 'startTime' | 'endTime'>,
  bandTime: string
): string {
  if (!shift.startTime || !shift.endTime) return bandTime;
  const endHour = (shift.startHour + shiftDurationHours(shift)) % 24;
  const pad = (hour: number) => String(hour).padStart(2, '0');
  return `${pad(shift.startHour)}:00–${pad(endHour)}:00`;
}

// Coverage/FTE deltas for assigning a candidate to an open slot, from real data: coverage
// is per-cell (filled → filled+1 of planned, identical for every candidate), FTE is per
// worker (remaining → remaining − shift hours). Null when an input is unknown (no demand
// for the cell, or the worker's hours aren't populated) ⇒ the footer is hidden.
export function candidateKpi(
  remainingHours: number | null,
  coverage: { before: number; target: number } | null,
  durationHours: number
): ScheduleCandidateKpiType | null {
  if (coverage === null || remainingHours === null) return null;
  return {
    coverage: {
      before: coverage.before,
      after: coverage.before + 1,
      target: coverage.target,
    },
    fteRemaining: {
      before: remainingHours,
      after: Math.max(0, remainingHours - durationHours),
    },
  };
}

// A candidate's applied constraints as worker-identity tags: a hard block reads as
// blocking, a soft penalty as caution.
export function reasonsToSignals(
  reasons: ScheduleShiftCandidateRowType['reasons']
): ScheduleCandidateSignalType[] {
  return reasons.map((reason) => ({
    kind: reason.hard ? 'blocking' : 'caution',
    label: reason.label,
  }));
}

export type ScheduleCandidateWithMemberType = {
  candidate: ScheduleShiftCandidateRowType;
  member: ScheduleTeamMemberType;
};

export type ScheduleCandidateBucketsType = {
  homeEligible: ScheduleCandidateWithMemberType[];
  homeNotEligible: ScheduleCandidateWithMemberType[];
  crossEligible: ScheduleCandidateWithMemberType[];
};

// Eligible buckets: most hours remaining to FTE first (needs hours most), nulls last, then
// name ascending as a stable tiebreaker.
function sortByHoursNeeded(
  entries: ScheduleCandidateWithMemberType[]
): ScheduleCandidateWithMemberType[] {
  return [...entries].sort((a, b) => {
    const aHours = a.member.remainingHours;
    const bHours = b.member.remainingHours;
    if (aHours === null && bHours === null)
      return a.member.name.localeCompare(b.member.name);
    if (aHours === null) return 1;
    if (bHours === null) return -1;
    if (bHours !== aHours) return bHours - aHours;
    return a.member.name.localeCompare(b.member.name);
  });
}

// Not-eligible bucket: alphabetical by name only.
function sortByName(
  entries: ScheduleCandidateWithMemberType[]
): ScheduleCandidateWithMemberType[] {
  return [...entries].sort((a, b) =>
    a.member.name.localeCompare(b.member.name)
  );
}

// Attaches each backend candidate to its roster member by the shared worker uuid, dropping
// any candidate not on the loaded roster (the same join the mock made, now keyed on id).
// Eligible buckets are sorted by hours remaining to FTE descending (who needs hours most),
// not-eligible by name ascending.
//
// `excludeWorkerId` drops the shift's own assigned/suggested worker from every bucket: the
// backend deliberately keeps them in the verdict set (the slot uses the real shift id, so the
// evaluator self-excludes their own row and they stay eligible) only so `assignedFitScore` can
// read their score off the raw buckets — they must not also be offered as a candidate for the
// shift they already hold. Null (open slots) excludes nobody.
export function enrichCandidateBuckets(
  buckets: ScheduleShiftCandidatesType,
  team: ScheduleTeamMemberType[],
  excludeWorkerId: string | null = null
): ScheduleCandidateBucketsType {
  const byId = new Map(team.map((member) => [member.id, member]));
  const enrich = (
    rows: ScheduleShiftCandidateRowType[]
  ): ScheduleCandidateWithMemberType[] =>
    rows
      .filter((candidate) => candidate.workerId !== excludeWorkerId)
      .map((candidate) => ({ candidate, member: byId.get(candidate.workerId) }))
      .filter(
        (entry): entry is ScheduleCandidateWithMemberType =>
          entry.member !== undefined
      );

  return {
    homeEligible: sortByHoursNeeded(enrich(buckets.homeEligible)),
    homeNotEligible: sortByName(enrich(buckets.homeNotEligible)),
    crossEligible: sortByHoursNeeded(enrich(buckets.crossEligible)),
  };
}

// Candidate sections to expand by default. Only Eligible from Home Unit opens, always — whether
// the shift is filled or open. Available from Other Units and Not Eligible from Home Unit stay
// collapsed until the scheduler expands them.
export function defaultCandidateSections(): string[] {
  return ['home'];
}

// Fit score of the assigned worker, when they appear in the recomputed candidate set.
export function assignedFitScore(
  workerId: string | null,
  buckets: ScheduleShiftCandidatesType | null
): number | null {
  if (!workerId || !buckets) return null;
  const all = [
    ...buckets.homeEligible,
    ...buckets.homeNotEligible,
    ...buckets.crossEligible,
  ];
  return (
    all.find((candidate) => candidate.workerId === workerId)?.fitScore ?? null
  );
}

// WORK-6515: per-constraint "why this move" copy — explains the constraint and the tradeoff
// rather than just naming the outcome. Keyed by constraint key; any key not listed (hard / term
// keys) falls back to its terse ConstraintLabels label. Scoped to this surface on purpose: the
// candidate-row signal chips reuse the terse backend labels via `reasonsToSignals` and stay short.
const WHY_THIS_MOVE_COPY: Record<string, string> = {
  role_penalty:
    'Assigned across roles because no qualified staff were available in the primary role while maintaining coverage.',
  consecutive_weekends:
    'Assigned despite consecutive weekends to meet coverage needs.',
  soft_block_conflict:
    'Assigned during a soft-blocked time because additional coverage was needed.',
};

// Shown when a Works-assigned shift had no applied constraints (a clean best-fit) — the
// "Why this move?" section explains the pick instead of being hidden (WORK-6515).
export const CLEAN_MATCH_REASON =
  'This worker was the strongest eligible match for the shift. No scheduling tradeoffs were required.';

// Applied constraint rationale for the move, hard blocks first then by penalty magnitude —
// the real "why this move" copy (no score; the grid surfaces none).
export function getMoveReasons(
  reasons: ScheduleAssignedReasonType[]
): string[] {
  return [...reasons]
    .sort((a, b) => {
      if (a.hard !== b.hard) return a.hard ? -1 : 1;
      return Math.abs(b.weight ?? 0) - Math.abs(a.weight ?? 0);
    })
    .map((reason) => WHY_THIS_MOVE_COPY[reason.key] ?? reason.label);
}

// "Why this move?" lines for the Shift Detail panel: the explanatory reasons, or the clean-match
// default for a Works (balancer) assignment that fired no constraints. Manual / pattern / self /
// float / open / pto shifts carry no assignedReasons and are not `balancer`, so they return [] and
// the section stays hidden (WORK-6505/6423: "Why this move?" is a Works-assigned surface only).
export function whyThisMoveReasons(
  shift: Pick<ScheduleShiftType, 'source' | 'assignedReasons'>
): string[] {
  const reasons = getMoveReasons(shift.assignedReasons ?? []);
  if (reasons.length > 0) return reasons;

  return shift.source === 'balancer' ? [CLEAN_MATCH_REASON] : [];
}
